import React, { useEffect, useState } from 'react';
import { HttpTransportType, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { Box, Modal, Typography } from '@material-ui/core';
import { INTAKE_API_BASE_URL } from 'middleware/intakeApi';
import auth from 'auth/auth';
import { Button } from '@mui/material';
import { SEND_MAINTENANCE_OBJECTION_FAILURE } from './maintenance.actions';
import { TOAST_MESSAGE_SEVERITY_ERROR } from 'modules/layout/types';
import SpinnerComponent from 'modules/common/spinner.component';

const MaintenanceMonitor = props => {
  const [maintenanceTime, setMaintenanceTime] = useState(null);
  const [isSiteDown, setIsSiteDown] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  const [countdown, setCountdown] = useState('');
  const [activeUsers, setActiveUsers] = useState([]);
  const [showActiveUsersModal, setShowActiveUsersModal] = useState(false);
  const [objectionSent, setObjectionSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let authToken = auth.getAccessToken();

    const connection = new HubConnectionBuilder()
      .withUrl(`${INTAKE_API_BASE_URL}maintenanceHub`, {
        accessTokenFactory: () => authToken,
        transport: HttpTransportType.ServerSentEvents | HttpTransportType.LongPolling
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Information)
      .build();

    connection.on('ReceiveMaintenanceStatus', timestamp => {
      if (timestamp) {
        const [year, month, day, hour, minute, second] = timestamp.split(/[- :T]/).map(Number);
        const maintenanceDate = new Date(Date.UTC(year, month - 1, day, hour, minute, second || 0));
        setMaintenanceTime(maintenanceDate);
      } else {
        setMaintenanceTime(null);
        setIsSiteDown(false);
        setWarningMessage('');
        setCountdown('');
      }
    });

    connection.on('ActiveUsers', users => {
      console.log('Active users received:', users);
      setActiveUsers(users);
    });

    connection
      .start()
      .then(() => console.log('Connected to SignalR hub'))
      .catch(err => console.error('SignalR connection error:', err));

    return () => {
      connection.stop();
    };
  }, []);

  useEffect(() => {
    if (!maintenanceTime) return;

    const interval = setInterval(() => {
      const utcNow = Date.now();
      const timeDifference = maintenanceTime - utcNow; // Difference in milliseconds
      if (timeDifference <= 0) {
        setIsSiteDown(true);
        setWarningMessage(
          'This site is down for emergency maintenance. \n Please check back in about 5 to 10 minutes to see if the site is back up. '
        );
        setCountdown('');
      } else if (timeDifference <= 30 * 60 * 1000) {
        setIsSiteDown(false);
        setWarningMessage(
          'This site needs to go down for emergency maintenance in a few minutes. \n Please finish what you are currently doing, make sure all your work is saved, and then log out. \n You can check back in about 5 to 15 minutes to see if the site is back up. '
        );

        const minutes = Math.floor((timeDifference / 1000 / 60) % 60);
        const seconds = Math.floor((timeDifference / 1000) % 60);
        setCountdown(`${minutes}m ${seconds}s Remaining`);
      } else {
        setIsSiteDown(false);
        setWarningMessage('');
        setCountdown('');
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [maintenanceTime]);

  const sendObjection = async () => {
    setIsLoading(true);
    const response = await props.sendMaintencanceObjection();
    if (response.type === SEND_MAINTENANCE_OBJECTION_FAILURE) {
      props.showToast('Failed to send objection', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      setObjectionSent(false);
      setIsLoading(false);
      return false;
    } else {
      setObjectionSent(true);
      setIsLoading(false);
      return true;
    }
  };

  const warningStyle = {
    position: 'sticky',
    top: '60px',
    left: '60px',
    right: 0,
    width: '100%',
    padding: '15px',
    backgroundColor: 'yellow',
    color: 'black',
    fontSize: '1.25rem',
    fontWeight: 'bold',
    zIndex: 1000,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '20px',
    boxSizing: 'border-box',
    borderBottom: '2px solid black'
  };

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '1000px',
    length: '1000px',
    padding: '50px',
    bgcolor: 'white',
    border: '8px solid red',
    borderRadius: '10px',
    boxShadow: 24,
    textAlign: 'center'
  };

  return (
    <>
      {warningMessage && !isSiteDown && (
        <Box style={warningStyle} display="flex" justifyContent="space-between" alignItems="center" padding="10px">
          <Typography variant="h6" style={{ flex: 1, textAlign: 'center' }}>
            {warningMessage}
            <strong>{countdown}</strong>
          </Typography>
          {!isLoading ? (
            <Button
              variant="contained"
              size="small"
              onClick={sendObjection}
              style={{
                backgroundColor: '#d3d3d3', // Light grey background
                color: 'black',
                marginLeft: 'auto',
                marginRight: '5px'
              }}
              disabled={objectionSent}
            >
              Object
            </Button>
          ) : (
            <SpinnerComponent />
          )}
          {auth.userHasRole('admin') && (
            <Button
              variant="contained"
              size="small"
              onClick={() => setShowActiveUsersModal(true)}
              style={{
                backgroundColor: '#d3d3d3', // Light grey background
                color: 'black',
                marginLeft: 'auto',
                marginRight: '15px'
              }}
            >
              Active Users
            </Button>
          )}
        </Box>
      )}
      <Modal open={isSiteDown} disableEscapeKeyDown disableBackdropClick>
        <Box sx={modalStyle}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'black' }}>
            {warningMessage}
          </Typography>
        </Box>
      </Modal>
      <Modal open={showActiveUsersModal} onClose={() => setShowActiveUsersModal(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 800, // Increased width
            bgcolor: 'background.paper',
            border: '2px solid #000',
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            maxHeight: '80vh',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {activeUsers.length === 0 ? (
            <Typography>No active users found.</Typography>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', borderBottom: '1px solid #ccc', padding: '10px' }}>User ID</th>
                  <th style={{ textAlign: 'left', borderBottom: '1px solid #ccc', padding: '10px' }}>Full Name</th>
                  <th style={{ textAlign: 'left', borderBottom: '1px solid #ccc', padding: '10px' }}>Email</th>
                </tr>
              </thead>
              <tbody>
                {activeUsers.map(user => (
                  <tr key={user.userId}>
                    <td style={{ padding: '10px' }}>{user.userId}</td>
                    <td style={{ padding: '10px' }}>{user.fullName}</td>
                    <td style={{ padding: '10px' }}>{user.email}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button variant="contained" color="primary" onClick={() => setShowActiveUsersModal(false)}>
              OK
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default MaintenanceMonitor;
